import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Button, Stack, Card, CardContent, Avatar } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MainMenu from "./MainMenu";
import { useNavigate } from "react-router-dom"; // Agrega este import

// Mensajes animados para simular una conversación larga sobre chatbots de Nara
const whatsappMessages = [
	{
		text: "¡Hola! 👋 ¿En qué puedo ayudarte hoy?",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
	{
		text: "Hola, quiero saber cómo puedo automatizar la atención en WhatsApp.",
		align: "left",
		bg: "#fff",
		color: "#23272f",
		ml: undefined,
		mr: "auto",
		border: "1px solid #e5e7eb",
	},
	{
		text: "Con Nara puedes crear tu propio chatbot y responder 24/7 de forma personalizada 🚀",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
	{
		text: "¿Necesito saber programar?",
		align: "left",
		bg: "#fff",
		color: "#23272f",
		ml: undefined,
		mr: "auto",
		border: "1px solid #e5e7eb",
	},
	{
		text: "¡Para nada! Nara tiene un constructor visual, solo arrastras y sueltas bloques para crear la conversación. 🤩",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
	{
		text: "¿Y puedo captar clientes automáticamente?",
		align: "left",
		bg: "#fff",
		color: "#23272f",
		ml: undefined,
		mr: "auto",
		border: "1px solid #e5e7eb",
	},
	{
		text: "¡Claro! El chatbot puede calificar leads, agendar citas y responder preguntas frecuentes sin que tengas que intervenir. 📈",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
	{
		text: "¿Puedo personalizar el flujo de la conversación?",
		align: "left",
		bg: "#fff",
		color: "#23272f",
		ml: undefined,
		mr: "auto",
		border: "1px solid #e5e7eb",
	},
	{
		text: "Sí, puedes personalizar mensajes, preguntas, respuestas y hasta integrar IA para conversaciones más naturales. 🤖✨",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
	{
		text: "¡Genial! ¿Cómo empiezo?",
		align: "left",
		bg: "#fff",
		color: "#23272f",
		ml: undefined,
		mr: "auto",
		border: "1px solid #e5e7eb",
	},
	{
		text: "Solo tienes que registrarte en nara.ai y conectar tu WhatsApp Business. ¡Listo para conversar! 🚀",
		align: "right",
		bg: "#25D366",
		color: "#fff",
		ml: "auto",
		mr: undefined,
		border: undefined,
	},
];

// Reemplaza TypewriterMessage y AnimatedWhatsappChat para que el globo crezca dinámicamente como en WebsiteChatbotInfo.js

function AnimatedWhatsappChat() {
    const [displayedMessages, setDisplayedMessages] = useState([]);
    const [typing, setTyping] = useState(false);
    const [currentMsgIdx, setCurrentMsgIdx] = useState(0);
    const [charIdx, setCharIdx] = useState(0);
    const [cycle, setCycle] = useState(0);
    const chatRef = useRef(null);

    useEffect(() => {
        let typingTimeout;
        let nextMsgTimeout;
        let resetTimeout;

        if (currentMsgIdx < whatsappMessages.length) {
            setTyping(true);
            const currentMsg = whatsappMessages[currentMsgIdx];
            if (charIdx < currentMsg.text.length) {
                typingTimeout = setTimeout(() => {
                    setDisplayedMessages((prev) => {
                        const newMsgs = [...prev];
                        if (newMsgs[currentMsgIdx]) {
                            newMsgs[currentMsgIdx].text = currentMsg.text.slice(0, charIdx + 1);
                        } else {
                            newMsgs.push({ ...currentMsg, text: currentMsg.text[0] });
                        }
                        return newMsgs;
                    });
                    setCharIdx((c) => c + 1);
                }, 22);
            } else {
                setTyping(false);
                nextMsgTimeout = setTimeout(() => {
                    setCurrentMsgIdx((idx) => idx + 1);
                    setCharIdx(0);
                }, 700);
            }
        } else {
            resetTimeout = setTimeout(() => {
                setDisplayedMessages([]);
                setCurrentMsgIdx(0);
                setCharIdx(0);
                setCycle((c) => c + 1);
            }, 9000);
        }

        return () => {
            clearTimeout(typingTimeout);
            clearTimeout(nextMsgTimeout);
            clearTimeout(resetTimeout);
        };
    }, [currentMsgIdx, charIdx, cycle]);

    // Scroll automático al final
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [displayedMessages]);

    return (
        <Box
            ref={chatRef}
            sx={{
                mb: 2,
                maxHeight: 420,
                overflowY: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                gap: 1,
            }}
        >
            {displayedMessages.map((msg, idx) => (
                <Box
                    key={idx}
                    sx={{
                        display: "flex",
                        justifyContent: msg.align === "right" ? "flex-end" : "flex-start",
                        alignItems: "flex-end",
                    }}
                >
                    <Box
                        sx={{
                            background: msg.bg,
                            color: msg.color,
                            border: msg.border,
                            borderRadius: 3,
                            px: 2.5,
                            py: 1.5,
                            maxWidth: 290,
                            fontSize: 15,
                            boxShadow:
                                msg.align === "right"
                                    ? "0 2px 8px #25D36644"
                                    : "0 2px 8px #0002",
                            ml: msg.align === "right" ? 0 : undefined,
                            mr: msg.align === "right" ? undefined : 0,
                            wordBreak: "break-word",
                            transition: "width 0.2s cubic-bezier(.4,2,.6,1)",
                            display: "inline-block",
                            minHeight: 28,
                        }}
                    >
                        {msg.text}
                    </Box>
                </Box>
            ))}
            {typing && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                bgcolor: "#25D366",
                                borderRadius: "50%",
                                animation: "blink 1s infinite alternate",
                                mx: 0.2,
                            }}
                        />
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                bgcolor: "#25D366",
                                borderRadius: "50%",
                                animation: "blink 1s infinite alternate 0.2s",
                                mx: 0.2,
                            }}
                        />
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                bgcolor: "#25D366",
                                borderRadius: "50%",
                                animation: "blink 1s infinite alternate 0.4s",
                                mx: 0.2,
                            }}
                        />
                    </Box>
                    <style>
                        {`
                @keyframes blink {
                  0% { opacity: 0.3; }
                  100% { opacity: 1; }
                }
              `}
                    </style>
                </Box>
            )}
        </Box>
    );
}

const benefits = [
	{
		icon: <AccessTimeIcon sx={{ color: "#38bdf8", fontSize: 38 }} />,
		title: "Atención instantánea 24/7",
		color: "#38bdf8",
		bg: "linear-gradient(120deg, #1e293b 0%, #334155 100%)",
		description:
			"Tus clientes reciben respuestas inmediatas en cualquier momento, sin depender de horarios ni agentes humanos.",
	},
	{
		icon: <ThumbUpAltIcon sx={{ color: "#6366f1", fontSize: 38 }} />,
		title: "Más ventas y leads calificados",
		color: "#6366f1",
		bg: "linear-gradient(120deg, #1e293b 0%, #6366f1 100%)",
		description:
			"El chatbot responde consultas, califica prospectos y agenda reuniones, ayudando a convertir más oportunidades en ventas.",
	},
	{
		icon: <SupportAgentIcon sx={{ color: "#34d399", fontSize: 38 }} />,
		title: "Ahorro de tiempo y recursos",
		color: "#34d399",
		bg: "linear-gradient(120deg, #1e293b 0%, #34d399 100%)",
		description:
			"Automatiza tareas repetitivas y permite que tu equipo se enfoque en lo realmente importante.",
	},
];

export default function WhatsappChatbotInfo() {
	const navigate = useNavigate(); // Hook para navegación

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "linear-gradient(120deg, #0f172a 0%, #1e293b 100%)",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* Fondo tech animado igual que LandingPage */}
			<Box sx={{
				position: "absolute",
				width: "100vw",
				height: "100vh",
				zIndex: 0,
				"&::before": {
					content: '""',
					position: "absolute",
					width: "180vw",
					height: "180vw",
					left: "-40vw",
					top: "-60vw",
					background:
						"radial-gradient(circle at 60% 40%, #6366f1 0%, transparent 60%), radial-gradient(circle at 30% 80%, #0ea5e9 0%, transparent 60%)",
					filter: "blur(120px)",
					opacity: 0.35,
					animation: "moveBg 18s linear infinite alternate",
				},
				"@keyframes moveBg": {
					"0%": { transform: "scale(1) translateY(0px)" },
					"100%": { transform: "scale(1.1) translateY(60px)" },
				},
			}} />
			{/* Partículas tech */}
			<Box sx={{
				position: "absolute",
				width: "100vw",
				height: "100vh",
				zIndex: 1,
				pointerEvents: "none",
			}}>
				{[...Array(14)].map((_, i) => (
					<Box
						key={i}
						sx={{
							position: "absolute",
							width: 6,
							height: 6,
							borderRadius: "50%",
							background: i % 2 === 0 ? "#38bdf8" : "#6366f1",
							opacity: 0.18 + (i % 3) * 0.08,
							left: `${Math.random() * 98}%`,
							top: `${Math.random() * 98}%`,
							filter: "blur(1.5px)",
							animation: `float${i % 3} 7s ease-in-out infinite alternate`,
							[`@keyframes float0`]: {
								"0%": { transform: "translateY(0)" },
								"100%": { transform: "translateY(-18px)" },
							},
							[`@keyframes float1`]: {
								"0%": { transform: "translateY(0)" },
								"100%": { transform: "translateY(12px)" },
							},
							[`@keyframes float2`]: {
								"0%": { transform: "translateY(0)" },
								"100%": { transform: "translateY(-10px)" },
							},
						}}
					/>
				))}
			</Box>
			<MainMenu />
			<Box sx={{ mt: 10, textAlign: "center", zIndex: 2, position: "relative" }} /> {/* Más espacio entre menú y sección principal */}
			{/* Título centrado arriba de la sección */}
			<Typography
				 variant="h3"
                 fontWeight={900}
                 color="#38bdf8" // Verde vibrante que destaca y combina con el fondo
                 align="center"
                 sx={{
                   letterSpacing: 2,
                   textShadow: "0 2px 16px #0ea5e9",
                   mb: { xs: 3, md: 5 },
                   mx: "auto",
                   maxWidth: 700,
                   display: "block", // Asegura que ocupe todo el ancho disponible
                 }}
			>
				WhatsApp con NARA ya no es solo mensajería.
			</Typography>

			{/* 1. Celular/texto */}
			<Box
				sx={{
					width: "100%",
					maxWidth: { xs: 360, sm: 700, md: 1100 }, // Igual que las otras secciones
					mx: "auto",
					minHeight: { xs: 480, md: 520 },
					background: "linear-gradient(120deg, #1e293b 0%, #334155 100%)",
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					alignItems: "center",
					justifyContent: "center",
					py: { xs: 3, md: 5 }, // Menos espacio arriba y abajo
					px: { xs: 2, md: 6 },
					mb: { xs: 4, md: 6 }, // Menos espacio inferior
					borderRadius: 4, // Igual que el hero principal
					boxShadow: "0 8px 32px 0 rgba(56,189,248,0.13)",
					position: "relative",
					zIndex: 2,
					gap: { xs: 4, md: 8 },
				}}
			>
				{/* Texto a la izquierda */}
				<Box
					sx={{
						flex: 1,
						minWidth: 280,
						color: "#cbd5e1",
						mr: { xs: 0, md: 6 },
						mb: { xs: 4, md: 0 },
						textAlign: { xs: "center", md: "left" },
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: { xs: "center", md: "flex-start" },
					}}
				>
					<Typography
						variant="h4"
						fontWeight={900}
						color="#25D366"
						mb={2}
						align="center"
						sx={{ letterSpacing: 1, textShadow: "0 2px 16px #25D36644" }}
					>
						Comunicación sin límites con NARA.
					</Typography>
					<Typography
						variant="body1"
						color="#cbd5e1"
						sx={{ fontSize: 18, mb: 2, whiteSpace: "pre-line" }}
					>
						Más que un chatbot. Es inteligencia conversacional en acción.
						{"\n\n"}
						Con NARA, no solo automatizas. Conectas, entiendes y conversas con inteligencia en el canal donde tus clientes ya están: WhatsApp.
						{"\n\n"}
						💬 Lanza campañas de marketing conversacional que no suenan a anuncio, sino que fluyen como diálogos genuinos con una IA que sí sabe escuchar.
						{"\n\n"}
						🤖 Combina lo mejor de la automatización con lo mejor de una conversación real. NARA responde, aprende, se adapta.
						{"\n\n"}
						🤝 Acompaña a tus clientes, responde en tiempo real y crea relaciones que escalan sin perder el toque humano.
						{"\n\n"}
						⚙️ Automatiza lo repetitivo, personaliza lo importante.
						{"\n\n"}
						Todo, desde la app más usada del planeta. Todo, desde una plataforma inteligente.
						{"\n\n"}
						Empieza a hablar con tus clientes hoy, y deja que esas conversaciones —potenciadas por IA— impulsen el crecimiento de mañana.
						{"\n\n"}
						{"\n\n"}
					</Typography>
					
					<Box
					sx={{
						position: "absolute",
						bottom: 24,
						left: 0,
						width: "100%",
						display: "flex",
						justifyContent: "center",
						zIndex: 3,
						pointerEvents: "auto",
					}}
					>
					<Button
						variant="contained"
						size="large"
						sx={{
						background: "linear-gradient(90deg, #25D366 0%, #38bdf8 100%)",
						fontWeight: 700,
						color: "#fff",
						borderRadius: 3,
						px: 5,
						py: 1.5,
						fontSize: 20,
						boxShadow: "0 2px 16px #25D36655",
						textTransform: "none",
						letterSpacing: 1,
						"&:hover": {
							background: "linear-gradient(90deg, #38bdf8 0%, #25D366 100%)",
							transform: "scale(1.04)",
						},
						}}
						onClick={() => navigate("/constructor")}
					>
						Probar NARA Chatbot
					</Button>
					</Box>
				</Box>
				{/* Celular animado a la derecha */}
				<Box
					sx={{
						width: 320,
						height: 640,
						borderRadius: "38px",
						boxShadow: "0 8px 32px 0 #25D36633, 0 2px 8px #0008",
						background: "linear-gradient(135deg, #075e54 0%, #25d366 100%)",
						border: "8px solid #e5e7eb",
						position: "relative",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-end",
						p: 0,
						flexShrink: 0,
						mx: { xs: "auto", md: 0 },
						mr: { xs: 0, md: 4 },
					}}
				>
					{/* Notch y altavoz */}
					<Box
						sx={{
							position: "absolute",
							top: 10,
							left: "50%",
							transform: "translateX(-50%)",
							width: 120,
							height: 18,
							background: "#222c32",
							borderRadius: "12px",
							zIndex: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Box
							sx={{
								width: 40,
								height: 6,
								background: "#444d52",
								borderRadius: 3,
							}}
						/>
					</Box>
					{/* Pantalla interna */}
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-end",
							background: "#fff",
							px: 2,
							pb: 2,
							pt: 6,
						}}
					>
						<AnimatedWhatsappChat />
						{/* Barra inferior simulada */}
						<Box
							sx={{
								height: 44,
								background: "#f0f0f0cc",
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								px: 2,
							}}
						>
							<Box
								sx={{
									flex: 1,
									background: "#fff",
									borderRadius: 2,
									height: 28,
									mx: 1,
								}}
							/>
							<Box sx={{ color: "#25D366", fontSize: 22, ml: 1 }}>🎤</Box>
							<Box sx={{ color: "#25D366", fontSize: 22, ml: 1 }}>📎</Box>
						</Box>
					</Box>
					{/* Botón home */}
					<Box
						sx={{
							position: "absolute",
							bottom: 12,
							left: "50%",
							transform: "translateX(-50%)",
							width: 48,
							height: 6,
							background: "#d1d5db",
							borderRadius: 3,
							zIndex: 2,
						}}
					/>
				</Box>
			</Box>

			{/* 2. Cards de beneficios */}
			<Box
				sx={{
					width: "100%",
					maxWidth: { xs: 360, sm: 700, md: 1100 },
					mx: "auto",
					mb: { xs: 6, md: 8 },
					px: { xs: 0, md: 0 },
				}}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={4}
					justifyContent="center"
					alignItems="stretch"
				>
					{benefits.map((benefit, idx) => (
						<Card
							key={idx}
							sx={{
								flex: 1,
								minWidth: 260,
								maxWidth: 360,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: 6,
								boxShadow: `0 8px 32px 0 ${benefit.color}33, 0 2px 8px #0002`,
								border: `2.5px solid ${benefit.color}`,
								background: `linear-gradient(135deg, ${benefit.bgStart || "#1e293b"} 0%, ${benefit.bgEnd || "#334155"} 100%)`,
								color: "#cbd5e1",
								minHeight: 260,
								mx: "auto",
								p: 0,
								overflow: "hidden",
								position: "relative",
								transition: "transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s",
								"&:hover": {
									transform: "scale(1.06) translateY(-10px)",
									boxShadow: `0 16px 48px 0 ${benefit.color}55, 0 4px 16px #0003`,
									borderColor: benefit.color,
								},
							}}
						>
							{/* Fondo decorativo animado */}
							<Box
								sx={{
									position: "absolute",
									top: -40,
									right: -40,
									width: 120,
									height: 120,
									background: `radial-gradient(circle at 60% 40%, ${benefit.color}33 0%, transparent 80%)`,
									zIndex: 0,
									filter: "blur(2px)",
									animation: "cardGlow 6s ease-in-out infinite alternate",
									"@keyframes cardGlow": {
										"0%": { opacity: 0.7, transform: "scale(1)" },
										"100%": { opacity: 1, transform: "scale(1.15)" },
									},
								}}
							/>
							{/* Icono espectacular */}
							<Avatar
								sx={{
									bgcolor: "#fff",
									width: 90,
									height: 90,
									boxShadow: `0 4px 24px ${benefit.color}44, 0 2px 8px #0002`,
									mt: 4,
									mb: 2,
									zIndex: 1,
									border: `3px solid ${benefit.color}`,
									transition: "transform 0.25s cubic-bezier(.4,2,.6,1)",
									transform: "scale(1.08)",
								}}
							>
								{/* Puedes usar un icono de Material UI más grande y colorido */}
								{React.cloneElement(benefit.icon, {
									sx: { fontSize: 48, color: benefit.color, filter: "drop-shadow(0 2px 8px #fff8)" }
								})}
							</Avatar>
							<CardContent
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									py: 3,
									px: 3,
									textAlign: "center",
									zIndex: 1,
								}}
							>
								<Typography
									variant="h6"
									fontWeight={900}
									color={benefit.color}
									mb={1}
									sx={{
										letterSpacing: 1,
										textShadow: `0 2px 12px ${benefit.color}44`,
										fontSize: 22,
									}}
								>
									{benefit.title}
								</Typography>
								<Typography
									variant="body1"
									color="#cbd5e1"
									sx={{
										opacity: 0.96,
										fontSize: 18,
										mt: 1,
										textShadow: "0 1px 6px #0002",
									}}
								>
									{benefit.description}
								</Typography>
							</CardContent>
						</Card>
					))}
				</Stack>
			</Box>

			{/* 3. Hero principal */}
			<Box
				sx={{
					mt: { xs: 6, md: 8 },
					textAlign: "center",
					zIndex: 2,
					position: "relative",
					maxWidth: { xs: 360, sm: 700, md: 1100 },
					mx: "auto",
					mb: { xs: 6, md: 8 },
				}}
			>
				<Paper
					elevation={8}
					sx={{
						p: { xs: 3, md: 6 },
						borderRadius: 4,
						maxWidth: "100%",
						mx: "auto",
						background: "rgba(255,255,255,0.98)",
						boxShadow: "0 8px 32px 0 rgba(56,189,248,0.13)",
					}}
				>
					<WhatsAppIcon sx={{ color: "#25D366", fontSize: 64, mb: 2 }} />
					<Typography
						variant="h3"
						fontWeight={900}
						color="#6366f1"
						mb={2}
						sx={{ letterSpacing: 1 }}
					>
						Chatbots para WhatsApp Business
					</Typography>
					<Typography
						variant="h5"
						color="#0ea5e9"
						fontWeight={700}
						sx={{ mb: 2 }}
					>
						Conversaciones inteligentes, atención 24/7 y más ventas para tu negocio.
					</Typography>
					<Typography
						variant="body1"
						color="#334155"
						sx={{ fontSize: 18, mb: 3 }}
					>
						Conecta tu WhatsApp Business a NARA y deja que un chatbot con IA atienda
						a tus clientes, responda preguntas frecuentes, califique leads y agende
						citas automáticamente. Ahorra tiempo, reduce costos y mejora la
						experiencia de tus usuarios.
					</Typography>
					<Button
						variant="contained"
						size="large"
						sx={{
							background: "linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)",
							fontWeight: 700,
							color: "#fff",
							borderRadius: 2,
							px: 5,
							py: 1.5,
							fontSize: 20,
							boxShadow: "0 2px 16px #38bdf8",
							textTransform: "none",
							letterSpacing: 1,
							"&:hover": {
								background: "linear-gradient(90deg, #0ea5e9 0%, #6366f1 100%)",
								transform: "scale(1.04)",
							},
						}}
						onClick={() => navigate("/constructor")}
					>
						Prueba gratis para WhatsApp
					</Button>
				</Paper>
			</Box>

			<Box
				sx={{
					maxWidth: 1200,
					mx: "auto",
					mt: { xs: 6, md: 10 },
					mb: { xs: 6, md: 10 },
					px: { xs: 2, md: 0 },
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					alignItems: "center",
					justifyContent: "center",
					gap: { xs: 4, md: 8 },
					zIndex: 2,
					position: "relative",
				}}
			>
				{/* Texto principal */}
				
			</Box>

			{/* Nueva sección: Creador del futuro */}
			<Box
  sx={{
    width: "100%",
    maxWidth: { xs: 360, sm: 700, md: 1100 },
    mx: "auto",
    my: { xs: 4, md: 8 },
    px: { xs: 2, md: 6 },
    py: { xs: 4, md: 6 },
    background: "linear-gradient(120deg, #334155 0%, #1e293b 100%)",
    borderRadius: 4,
    boxShadow: "0 8px 32px 0 #38bdf855",
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: "center",
    justifyContent: "center",
    gap: { xs: 3, md: 6 },
    zIndex: 2,
    position: "relative",
    mb: { xs: 6, md: 10 },
  }}
>
  {/* Texto */}
  <Box sx={{ flex: 2, color: "#fff", textAlign: { xs: "center", md: "left" } }}>
    <Typography
      variant="h4"
      fontWeight={900}
      color="#38bdf8"
      sx={{
        mb: 2,
        letterSpacing: 1,
        textShadow: "0 2px 16px #38bdf844",
      }}
    >
      🚀 Creador del futuro, sin escribir una sola línea
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontSize: 20,
        color: "#cbd5e1",
        textShadow: "0 1px 8px #0004",
      }}
    >
      Imagina construir experiencias inteligentes con solo arrastrar y soltar. Diseña flujos dinámicos con lógica avanzada, fórmulas potentes y contenidos visuales envolventes. Todo desde una interfaz intuitiva que transforma ideas en realidad... sin código, pero con toda la magia de la innovación.
    </Typography>
  </Box>
  {/* Icono */}
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mt: { xs: 3, md: 0 },
    }}
  >
    <Avatar
      sx={{
        bgcolor: "#fff",
        width: 110,
        height: 110,
        boxShadow: "0 4px 32px #38bdf855, 0 2px 8px #0002",
        border: "4px solid #38bdf8",
        transition: "transform 0.25s cubic-bezier(.4,2,.6,1)",
        transform: "scale(1.08)",
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: 64, color: "#38bdf8", filter: "drop-shadow(0 2px 12px #38bdf844)" }} />
    </Avatar>
  </Box>
</Box>
		</Box>
	);
}